import { httpResource } from '@angular/common/http';
import { effect, inject, Injectable, signal, untracked } from '@angular/core';

import { AuthStore } from '@rpgc-ai/store/auth/auth.store';
import { ProfileStore } from '@rpgc-ai/store/profile/profile.store';

import type { IBalance, IImageGenerationSettings, IProfile } from '@rpgc-ai/shared-interfaces/interfaces/profile.interface';
import { IS_SERVER } from '@rpgc-ai/shared-interfaces/tokens/is-server.token';

import { TranslocoService } from '@jsverse/transloco';
import { isEmpty } from 'lodash-es';

import { CharactersService } from '../characters/characters.service';
import { NotificationsService } from '../notifications/notifications.service';
import { StorageService } from '../storage/storage.service';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  // Injections
  readonly #isServer = inject(IS_SERVER);

  readonly #charactersService = inject(CharactersService);
  readonly #notificationsService = inject(NotificationsService);
  readonly #translocoService = inject(TranslocoService);

  readonly #authStore = inject(AuthStore);
  readonly #profileStore = inject(ProfileStore);
  readonly #storageService = inject(StorageService);

  // Variables
  readonly #inviteCode = signal<string | null>(null);
  readonly #imageGenerationSettings = signal<IImageGenerationSettings | null>(null);

  /** Флаг для блокировки автозапроса */
  readonly #isLoadAllowedProfileResource = signal<boolean>(false);
  /** триггер для загрузки информации о балансе */
  readonly #isGettingBalanceAllowed = signal<boolean>(false);

  readonly updateProfileBody = signal<Partial<IProfile> | null>(null);
  readonly updateProfileSuccessful = signal<boolean>(false);

  /** Флаг изменения слага пользователя */
  #isUserSlugChanged = false;

  /** Внутренняя синхронизация профиля идёт без ведома пользователя, поэтому и без тостов о сохранении. */
  #isSilentProfileUpdate = false;

  /**
   * Получение профиля
   */
  readonly profileResource = httpResource<IProfile>(() => {
    // ВАЖНО: НЕ используем untracked() для isLoadAllowed, чтобы httpResource реагировал на изменения
    const isLoadAllowed = this.#isLoadAllowedProfileResource();
    const session = this.#authStore.session();

    // Запрос выполняется только если:
    // 1. Загрузка разрешена (isLoadAllowed = true)
    // 2. Есть активная сессия (пользователь авторизован)
    if (!isLoadAllowed || !session) {
      return undefined;
    }

    return {
      url: 'profile/me',
      method: 'GET',
    };
  });

  readonly #balanceResource = httpResource<IBalance>(() => {
    const isGettingBalanceAllowed = this.#isGettingBalanceAllowed();
    if (!isGettingBalanceAllowed) {
      return;
    }

    return {
      url: 'finance/balance',
      method: 'GET',
    };
  });

  /**
   * Проверка кода приглашения
   */
  readonly validateInviteCodeResource = httpResource<{ message: string; success: boolean }>(() => {
    const invitationCode = this.#inviteCode();

    if (!invitationCode) {
      return;
    }

    return {
      url: 'profile/validate-invitation-code',
      method: 'POST',
      body: {
        invitationCode,
      },
    };
  });

  readonly #imagGenerationSettingsResource = httpResource<IProfile>(() => {
    const imageGenerationSettings = this.#imageGenerationSettings();

    if (!imageGenerationSettings) {
      return;
    }

    return {
      url: 'profile/me/image-generation-settings',
      method: 'PATCH',
      body: {
        ...imageGenerationSettings,
      },
    };
  });

  /** HttpResource обновления профиля */
  readonly updateProfileResource = httpResource<IProfile>(() => {
    const session = this.#authStore.session();
    const body = this.updateProfileBody();

    if (!session || !body || isEmpty(body)) {
      return;
    }

    if (body.slug) {
      this.#isUserSlugChanged = true;
    }

    return {
      url: 'profile/me',
      method: 'PATCH',
      body,
    };
  });

  constructor() {
    this.#autoLoadProfileEffect();
    this.#loadedProfileEffect();
    this.#updateProfileEffect();
    this.#updateProfileImageGenerationSettingsEffect();
    this.#updateProfileBalanceEffect();
    // Обновляыем поле isAdultConfirmed
    this.#updateIsAdultConfirmedEffect();
  }

  updateProfile(profile: Partial<IProfile>): void {
    //reset profile resource for work
    this.clearServiceState();
    this.updateProfileBody.set(profile);
  }

  /**
   * Получение профиля
   */
  getProfile(): boolean {
    const currentProfile = this.#profileStore.profile();

    // На клиенте сначала проверяем, не загружен ли уже профиль из переданного состояния
    if (!this.#isServer && !!currentProfile?.id) {
      return false;
    }

    return this.forceReloadProfile();
  }

  /**
   * Принудительная перезагрузка профиля (игнорируя переданное состояние)
   */
  forceReloadProfile(): boolean {
    this.#isLoadAllowedProfileResource.set(true);
    return this.profileResource.reload();
  }

  getUserBalance(): void {
    this.#isGettingBalanceAllowed.set(true);
  }

  updateProfileImageGenerationSettings(newSettings: IImageGenerationSettings): void {
    this.#imageGenerationSettings.set(newSettings);
  }

  updateProfileNsfwGenerationSetting(isNSFW: boolean): void {
    const profile = this.#profileStore.profile();
    if (!profile?.imageGenerationStyle) {
      return;
    }
    this.#imageGenerationSettings.set({
      guidanceScore: profile?.imageGenerationGuidanceScore ?? 7.5,
      style: profile?.imageGenerationStyle ?? '',
      negativePrompt: profile?.imageGenerationNegativePrompt ?? '',
      isNSFW,
    });
  }

  /**
   * Отправка инвайт кода
   */
  sendInviteCode(code: string): void {
    this.#inviteCode.set(code);
  }

  /**
   * Обновление фильтра нсфв
   */
  updateNsfwFilter(isSafeSearch: boolean): void {
    // обновляем стор
    this.#profileStore.setUserNsfwFilter(isSafeSearch);
  }

  /**
   * Очистка профиля (при логауте)
   */
  clearProfile(): void {
    this.#profileStore.clearProfile();
  }

  clearServiceState(): void {
    this.updateProfileBody.set(null);
    this.updateProfileSuccessful.set(false);
  }

  /**
   * Эффект загрузки профиля
   */
  #loadedProfileEffect(): void {
    effect(() => {
      const isLoading = this.profileResource.isLoading();
      const error = this.profileResource.error();
      const hasValue = this.profileResource.hasValue();
      const isLoadAllowed = this.#isLoadAllowedProfileResource();

      // Обновляем состояние загрузки в store
      untracked(() => {
        if (!isLoadAllowed) {
          return;
        }

        this.#profileStore.setLoading(isLoading);

        // Обработка ошибки
        if (error) {
          this.#profileStore.setError(error.message || 'Error profile loading');
          return;
        }

        // Успешная загрузка
        if (hasValue && !isLoading) {
          const profile = this.profileResource.value();
          this.#profileStore.setProfile(profile);
        }
      });
    });
  }

  /**
   * Автозагрузка профиля при изменении сессии
   */
  #autoLoadProfileEffect(): void {
    effect(() => {
      const session = this.#authStore.session();
      untracked(() => {
        if (!session) {
          return;
        }

        this.getProfile();
      });
    });
  }

  #updateProfileEffect(): void {
    effect(() => {
      const isLoading = this.updateProfileResource.isLoading();
      const error = this.updateProfileResource.error();
      const hasValue = this.updateProfileResource.hasValue();

      untracked(() => {
        // Обработка ошибки
        if (error) {
          this.#profileStore.setError(error.message || 'Error updating profile');
          if (!this.#isSilentProfileUpdate) {
            this.#notificationsService.addErrorNotification(
              this.#translocoService.translate('MODALS.PROFILE_SETTINGS.PREFERENCES.NOTIFICATIONS.FAIL'),
            );
          }
          // Очищаем сигнал обновления
          this.clearServiceState();

          this.#isUserSlugChanged = false;
          this.#isSilentProfileUpdate = false;

          return;
        }

        // Успешное обновление
        if (hasValue && !isLoading) {
          const updatedProfile = this.updateProfileResource.value();

          this.#profileStore.setProfile(updatedProfile);
          this.updateProfileSuccessful.set(true);

          if (this.#isUserSlugChanged) {
            this.#charactersService.clearCache();
          }

          // Очищаем сигнал обновления
          this.updateProfileBody.set(null);
          this.#isUserSlugChanged = false;

          if (!this.#isSilentProfileUpdate) {
            this.#notificationsService.addSuccessNotification(
              this.#translocoService.translate('MODALS.PROFILE_SETTINGS.PREFERENCES.NOTIFICATIONS.SUCCESS'),
            );
          }

          this.#isSilentProfileUpdate = false;
        }
      });
    });
  }

  #updateProfileImageGenerationSettingsEffect(): void {
    effect(() => {
      const isLoading = this.#imagGenerationSettingsResource.isLoading();
      const error = this.#imagGenerationSettingsResource.error();
      const hasValue = this.#imagGenerationSettingsResource.hasValue();

      untracked(() => {
        if (error) {
          this.#profileStore.setError(error.message || 'Error updating profile');
          return;
        }

        if (hasValue && !isLoading) {
          const updatedProfile = this.#imagGenerationSettingsResource.value();

          this.#profileStore.setProfile(updatedProfile);
          this.#imageGenerationSettings.set(null);
          this.#notificationsService.addSuccessNotification(
            this.#translocoService.translate('MODALS.PROFILE_SETTINGS.PREFERENCES.NOTIFICATIONS.SUCCESS'),
          );
        }
      });
    });
  }

  #updateProfileBalanceEffect(): void {
    effect(() => {
      const { error, isLoading, value } = this.#balanceResource;
      const isAllowed = this.#isGettingBalanceAllowed();
      if (isLoading() || !isAllowed) {
        return;
      }

      untracked(() => {
        const balance = value();
        if (error() || !balance) {
          this.#isGettingBalanceAllowed.set(false);
          return;
        }

        this.#isGettingBalanceAllowed.set(false);
        this.#profileStore.updateProfile({ balance }, { ignoreSync: true });
      });
    });
  }

  #updateIsAdultConfirmedEffect(): void {
    effect(() => {
      const isAuth = this.#authStore.isAuthenticated();
      if (!isAuth) {
        return;
      }

      untracked(() => {
        const isAdultConfirmed = this.#storageService.isAdultConfirmedValid();
        const backendIsAdultConfirmed = this.#profileStore.profile()?.isAdultConfirmed;

        if (isAdultConfirmed && !backendIsAdultConfirmed) {
          this.#isSilentProfileUpdate = true;
          this.updateProfile({ isAdultConfirmed: true });
        }
      });
    });
  }
}
